import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { CHATMESSAGEALL } from "../../services/actions";
import {
  Box, Grid, Typography, TextField, IconButton, Avatar, Badge,
  Chip, CircularProgress, Tooltip, Divider, Paper, Container, Popover
} from "@mui/material";
import { BsSendFill, BsSearch } from "react-icons/bs";
import { MdWork, MdTask } from "react-icons/md";
import VoiceMicButton from "../../components/VoiceMicButton";

// ── Avatar color helper ─────────────────────────────────────
const avatarColor = (name = "") => {
  const colors = ["#2563eb", "#7c3aed", "#059669", "#dc2626", "#d97706", "#0891b2"];
  return colors[name.charCodeAt(0) % colors.length];
};

// ── Time format helper ──────────────────────────────────────
const timeAgo = (dt) => {
  if (!dt) return "";
  const diff = (Date.now() - new Date(dt)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dt).toLocaleDateString();
};

export default function ChatView() {
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector((s) => s.loginState?.auth);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [taggedWork, setTaggedWork] = useState(null);
  const [taggedTask, setTaggedTask] = useState(null);
  const [works, setWorks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [autocomplete, setAutocomplete] = useState({ open: false, type: null, query: "", items: [] });
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [pickerType, setPickerType] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [unreadMap, setUnreadMap] = useState({});
  const [userSearch, setUserSearch] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);

  // ── Bulletproof URL builder ────────────────────────────────
  const getApiUrl = (endpoint) => {
    let base = process.env.REACT_APP_URL || "http://localhost:8000/api/";
    if (!base.endsWith("/")) base += "/";
    let p = endpoint;
    if (p.startsWith("/")) p = p.slice(1);
    return base + p;
  };

  // ── Load background data directly via Axios ────────────────
  useEffect(() => {
    if (!auth?.token) return;
    const headers = { Authorization: `Bearer ${auth.token}` };

    // Fetch Users
    axios.get(getApiUrl('public/user/0/?filter_by={"is_active":true}'), { headers })
      .then((res) => {
        const raw = res.data?.data || res.data;
        let all = Array.isArray(raw) ? raw : (raw?.results || raw?.data || []);
        if (!Array.isArray(all) && typeof raw === "object") {
          all = Object.values(raw).find(Array.isArray) || [];
        }
        setUsers(all.filter((u) => Number(u.id) !== Number(auth?.id)));
      }).catch(console.error);

    // Fetch Works
    axios.get(getApiUrl("work/0/"), { headers })
      .then((res) => {
        const raw = res.data?.data || res.data;
        setWorks(Array.isArray(raw) ? raw : (raw?.results || []));
      }).catch(() => {});

    // Fetch Tasks
    axios.get(getApiUrl("task/0/"), { headers })
      .then((res) => {
        const raw = res.data?.data || res.data;
        setTasks(Array.isArray(raw) ? raw : (raw?.results || []));
      }).catch(() => {});
  }, [auth?.token]);

  // ── Load unread map directly via Axios ─────────────────────
  const fetchUnread = () => {
    if (!auth?.token) return;
    axios.get(getApiUrl("chat/unread_count/"), {
      headers: { Authorization: `Bearer ${auth.token}` }
    }).then((res) => {
      const data = res.data?.data || res.data;
      if (data?.per_user) {
        const map = {};
        data.per_user.forEach((u) => { map[Number(u.sender_id)] = u.count; });
        setUnreadMap(map);
      }
    }).catch(() => {});
  };

  useEffect(() => { fetchUnread(); }, [auth?.token]);

  // ── Load conversation directly via Axios ───────────────────
  useEffect(() => {
    if (!selectedUser || !auth?.token) return;
    axios.get(getApiUrl(`chat/?recipient=${selectedUser}`), {
      headers: { Authorization: `Bearer ${auth.token}` }
    }).then((res) => {
      const data = res.data?.data || res.data;
      const arr = Array.isArray(data) ? data : (data?.results || []);
      setMessages(arr);
    }).catch(console.error);
  }, [selectedUser, auth?.token]);

  // ── SSE listener ──────────────────────────────────────────
  useEffect(() => {
    if (!auth?.id) return;
    const url = getApiUrl(`sse/stream/${auth.id}/`);
    const es = new EventSource(url);
    es.onmessage = (e) => {
      try {
        const raw = JSON.parse(e.data);
        const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (msg.event === "chat" && msg.message_type === "chat") {
          const senderId = Number(msg.sender);
          // Real-time: if from currently selected user, append & mark read
          if (senderId === Number(selectedUser)) {
            setMessages((prev) => [...prev, msg]);
            if (auth?.token) {
              axios.post(getApiUrl("chat/mark_read/"), { sender_id: senderId }, {
                headers: { Authorization: `Bearer ${auth.token}` }
              }).then(() => {
                dispatch({ type: `${CHATMESSAGEALL}_GET`, payload: "chat/unread_count/" });
              }).catch(() => {});
            }
          } else {
            // Update unread badge
            setUnreadMap((prev) => ({
              ...prev,
              [senderId]: (prev[senderId] || 0) + 1,
            }));
            dispatch({ type: `${CHATMESSAGEALL}_GET`, payload: "chat/unread_count/" });
          }
        }
      } catch (_) {}
    };
    return () => es.close();
  }, [auth?.id, auth?.token, selectedUser, dispatch]);

  // ── Auto-scroll ────────────────────────────────────────────
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Select user ────────────────────────────────────────────
  const selectUser = (userId) => {
    const numId = Number(userId);
    setSelectedUser(numId);
    setMessages([]);
    // Mark messages as read on backend
    if (auth?.token) {
      axios.post(getApiUrl("chat/mark_read/"), { sender_id: numId }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      }).then(() => {
        dispatch({ type: `${CHATMESSAGEALL}_GET`, payload: "chat/unread_count/" });
      }).catch(() => {});
    }
    setUnreadMap((prev) => ({ ...prev, [numId]: 0 }));
  };

  // ── Auto-select user from location state ───────────────────
  useEffect(() => {
    const target = location.state?.selectedUser;
    if (target && Number(selectedUser) !== Number(target)) {
      selectUser(target);
    }
  }, [location.state?.selectedUser]);

  // ── Input handler with # and @ detection ──────────────────
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);

    const lastWord = val.split(/\s/).pop();
    if (lastWord.startsWith("#")) {
      const q = lastWord.slice(1).toLowerCase();
      setAutocomplete({
        open: true, type: "work", query: q,
        items: works.filter((w) => w.work_name?.toLowerCase().includes(q)).slice(0, 6),
      });
    } else if (lastWord.startsWith("@")) {
      const q = lastWord.slice(1).toLowerCase();
      setAutocomplete({
        open: true, type: "task", query: q,
        items: tasks.filter((t) => t.name?.toLowerCase().includes(q)).slice(0, 6),
      });
    } else {
      setAutocomplete({ open: false, type: null, query: "", items: [] });
    }
  };

  const applyAutocomplete = (item) => {
    if (autocomplete.type === "work") {
      setTaggedWork(item);
      setInput((v) => v.replace(/#\S*$/, `#${item.work_name} `));
    } else {
      setTaggedTask(item);
      setInput((v) => v.replace(/@\S*$/, `@${item.name} `));
    }
    setAutocomplete({ open: false, type: null, query: "", items: [] });
    inputRef.current?.focus();
  };

  // ── Send message ──────────────────────────────────────────
  const sendMessage = () => {
    if (!input.trim() || !selectedUser || !auth?.token) return;
    const payload = {
      content: input.trim(),
      recipient: selectedUser,
      message_type: "chat",
      tagged_work_id: taggedWork?.id || null,
      tagged_work_name: taggedWork?.work_name || null,
      tagged_task_id: taggedTask?.id || null,
      tagged_task_name: taggedTask?.name || null,
    };
    // Optimistic: add to local messages
    setMessages((prev) => [
      ...prev,
      { ...payload, sender: auth?.id, sender_name: auth?.first_name, created_at: new Date().toISOString(), id: Date.now() }
    ]);
    // Direct Axios Post
    axios.post(getApiUrl("chat/"), payload, {
      headers: { Authorization: `Bearer ${auth.token}` }
    }).catch(console.error);

    setInput("");
    setTaggedWork(null);
    setTaggedTask(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === "Escape") setAutocomplete({ open: false, type: null, query: "", items: [] });
  };

  // ── Picker (modal select) ──────────────────────────────────
  const openPicker = (e, type) => {
    setPickerAnchor(e.currentTarget);
    setPickerType(type);
    setPickerSearch("");
  };
  const closePicker = () => { setPickerAnchor(null); setPickerType(null); };
  const pickerItems = pickerType === "work"
    ? works.filter((w) => w.work_name?.toLowerCase().includes(pickerSearch.toLowerCase()))
    : tasks.filter((t) => t.name?.toLowerCase().includes(pickerSearch.toLowerCase()));

  const selectedUserObj = users.find((u) => Number(u.id) === Number(selectedUser));
  const filteredUsers = users.filter((u) =>
    `${u.first_name} ${u.last_name || ""} ${u.username || ""}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <Container maxWidth="false" sx={{ p: 0, height: "calc(100vh - 80px)", display: "flex" }}>
      <Grid container sx={{ height: "100%", width: "100%", flexWrap: "nowrap" }}>

        {/* ── LEFT: User List ──────────────────────────────────── */}
        <Grid item xs={12} sm={3.5} sx={{
          borderRight: "1px solid #e2e8f0",
          display: "flex", flexDirection: "column",
          background: "#ffffff",
          width: { xs: "100%", sm: "30%" },
          minWidth: 260
        }}>
          {/* Header */}
          <Box sx={{ p: 2.5, borderBottom: "1px solid #f1f5f9" }}>
            <Typography variant="h6" fontWeight={700} color="#0f172a" fontSize={15}>
              Messages
            </Typography>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1, mt: 1.5,
              background: "#f8fafc", borderRadius: "10px", px: 1.5, py: 0.8,
              border: "1px solid #e2e8f0"
            }}>
              <BsSearch size={13} color="#94a3b8" />
              <input
                style={{ border: "none", background: "transparent", outline: "none", fontSize: 13, flex: 1, color: "#334155" }}
                placeholder="Search people..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </Box>
          </Box>

          {/* User list */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {filteredUsers.map((user) => {
              const uId = Number(user.id);
              return (
                <Box
                  key={uId}
                  onClick={() => selectUser(uId)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2, py: 1.5, cursor: "pointer",
                    background: selectedUser === uId ? "rgba(37,99,235,0.06)" : "transparent",
                    borderLeft: selectedUser === uId ? "3px solid #2563eb" : "3px solid transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { background: "#f8fafc" }
                  }}
                >
                  <Avatar sx={{
                    width: 38, height: 38, fontSize: 13, fontWeight: 700,
                    background: avatarColor(user.first_name || "U")
                  }}>
                    {user.first_name?.slice(0, 2)?.toUpperCase() || "U"}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={600} fontSize={13.5} color="#1e293b" noWrap>
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography fontSize={11.5} color="#94a3b8" noWrap>{user.username}</Typography>
                  </Box>
                  {unreadMap[uId] > 0 && (
                    <Box sx={{
                      background: "#2563eb", color: "#fff",
                      borderRadius: "50%", minWidth: 20, height: 20,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700
                    }}>
                      {unreadMap[uId]}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* ── RIGHT: Chat Area ─────────────────────────────────── */}
        <Grid item xs={12} sm={8.5} sx={{ display: "flex", flexDirection: "column", background: "#f8fafc", flex: 1 }}>

          {selectedUser ? (
            <>
              {/* Chat Header */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: 2, px: 3, py: 2,
                background: "#ffffff", borderBottom: "1px solid #e2e8f0"
              }}>
                <Avatar sx={{
                  width: 38, height: 38, fontSize: 13, fontWeight: 700,
                  background: avatarColor(selectedUserObj?.first_name || "")
                }}>
                  {selectedUserObj?.first_name?.slice(0, 2)?.toUpperCase() || "U"}
                </Avatar>
                <Box>
                  <Typography fontWeight={700} fontSize={14} color="#0f172a">
                    {selectedUserObj?.first_name} {selectedUserObj?.last_name}
                  </Typography>
                  <Typography fontSize={12} color="#22c55e">● Active</Typography>
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {messages.map((msg, i) => {
                  const isMine = Number(msg.sender) === Number(auth?.id);
                  return (
                    <Box key={msg.id || i} sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                      {!isMine && (
                        <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: 11, fontWeight: 700, background: avatarColor(msg.sender_name || "") }}>
                          {msg.sender_name?.slice(0, 2)?.toUpperCase() || "U"}
                        </Avatar>
                      )}
                      <Box sx={{ maxWidth: "65%" }}>
                        <Box sx={{
                          px: 2, py: 1.2,
                          background: isMine ? "linear-gradient(135deg, #2563eb, #7c3aed)" : "#ffffff",
                          color: isMine ? "#fff" : "#1e293b",
                          borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          fontSize: 13.5,
                          lineHeight: 1.5,
                        }}>
                          <Typography fontSize={13.5} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {msg.content}
                          </Typography>
                          {/* Tagged Work chip */}
                          {msg.tagged_work_id && (
                            <Chip
                              icon={<MdWork size={12} />}
                              label={msg.tagged_work_name}
                              size="small"
                              sx={{ mt: 0.8, background: isMine ? "rgba(255,255,255,0.2)" : "#dbeafe", color: isMine ? "#fff" : "#1d4ed8", fontSize: 11 }}
                            />
                          )}
                          {/* Tagged Task chip */}
                          {msg.tagged_task_id && (
                            <Chip
                              icon={<MdTask size={12} />}
                              label={msg.tagged_task_name}
                              size="small"
                              sx={{ mt: 0.8, ml: 0.5, background: isMine ? "rgba(255,255,255,0.2)" : "#f3e8ff", color: isMine ? "#fff" : "#7c3aed", fontSize: 11 }}
                            />
                          )}
                        </Box>
                        <Typography fontSize={10.5} color="#94a3b8" sx={{ mt: 0.3, textAlign: isMine ? "right" : "left" }}>
                          {timeAgo(msg.created_at)}
                        </Typography>
                      </Box>
                      {isMine && (
                        <Avatar sx={{ width: 28, height: 28, ml: 1, fontSize: 11, fontWeight: 700, background: avatarColor(auth?.first_name || "") }}>
                          {auth?.first_name?.slice(0, 2)?.toUpperCase() || "ME"}
                        </Avatar>
                      )}
                    </Box>
                  );
                })}
                <div ref={endRef} />
              </Box>

              {/* Input area */}
              <Box sx={{ px: 3, pb: 2 }}>
                {/* Tagged chips preview */}
                {(taggedWork || taggedTask) && (
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    {taggedWork && (
                      <Chip
                        icon={<MdWork size={13} />}
                        label={taggedWork.work_name}
                        onDelete={() => setTaggedWork(null)}
                        size="small"
                        sx={{ background: "#dbeafe", color: "#1d4ed8", fontSize: 12 }}
                      />
                    )}
                    {taggedTask && (
                      <Chip
                        icon={<MdTask size={13} />}
                        label={taggedTask.name}
                        onDelete={() => setTaggedTask(null)}
                        size="small"
                        sx={{ background: "#f3e8ff", color: "#7c3aed", fontSize: 12 }}
                      />
                    )}
                  </Box>
                )}

                {/* Autocomplete dropdown */}
                {autocomplete.open && autocomplete.items.length > 0 && (
                  <Paper elevation={4} sx={{
                    mb: 1, borderRadius: "12px", overflow: "hidden",
                    border: "1px solid #e2e8f0", maxHeight: 220, overflowY: "auto"
                  }}>
                    {autocomplete.items.map((item, idx) => (
                      <Box
                        key={idx}
                        onClick={() => applyAutocomplete(item)}
                        sx={{
                          px: 2, py: 1.2, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                          "&:hover": { background: "#f8fafc" },
                          borderBottom: "1px solid #f1f5f9"
                        }}
                      >
                        {autocomplete.type === "work"
                          ? <MdWork size={16} color="#2563eb" />
                          : <MdTask size={16} color="#7c3aed" />
                        }
                        <Typography fontSize={13}>
                          {autocomplete.type === "work" ? item.work_name : item.name}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Input box */}
                <Box sx={{
                  display: "flex", alignItems: "flex-end", gap: 1,
                  background: "#ffffff", borderRadius: "14px",
                  border: "1px solid #e2e8f0", px: 1.5, py: 1,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                }}>
                  {/* Attachment picker buttons */}
                  <Tooltip title="Tag a Work (#)">
                    <IconButton size="small" onClick={(e) => openPicker(e, "work")}
                      sx={{ color: "#2563eb", "&:hover": { background: "#dbeafe" } }}>
                      <MdWork size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Tag a Task (@)">
                    <IconButton size="small" onClick={(e) => openPicker(e, "task")}
                      sx={{ color: "#7c3aed", "&:hover": { background: "#f3e8ff" } }}>
                      <MdTask size={18} />
                    </IconButton>
                  </Tooltip>

                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    multiline
                    maxRows={4}
                    variant="standard"
                    placeholder="Type a message... (use # for Work, @ for Task)"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    InputProps={{ disableUnderline: true, style: { fontSize: 13.5, paddingBottom: 4 } }}
                  />

                  <VoiceMicButton
                    initialText={input}
                    onTranscript={(text) => setInput(text)}
                    size={17}
                  />

                  <IconButton
                    onClick={sendMessage}
                    disabled={!input.trim()}
                    sx={{
                      background: input.trim() ? "linear-gradient(135deg, #2563eb, #7c3aed)" : "#e2e8f0",
                      color: "#fff", width: 36, height: 36,
                      "&:hover": { transform: "scale(1.05)" },
                      transition: "all 0.2s ease"
                    }}
                  >
                    <BsSendFill size={15} />
                  </IconButton>
                </Box>
                <Typography fontSize={11} color="#94a3b8" sx={{ mt: 0.5, textAlign: "center" }}>
                  Press Enter to send · Shift+Enter for newline · Type # for Work · Type @ for Task
                </Typography>
              </Box>
            </>
          ) : (
            // Empty state
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
              <Box sx={{ width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg, #dbeafe, #f3e8ff)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BsSendFill size={32} color="#2563eb" />
              </Box>
              <Typography fontWeight={600} color="#475569" fontSize={15}>Select a conversation</Typography>
              <Typography color="#94a3b8" fontSize={13}>Choose a person from the left to start chatting</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* ── Picker Popover ─────────────────────────────────────── */}
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "12px", width: 280, maxHeight: 320, border: "1px solid #e2e8f0" } }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid #f1f5f9" }}>
          <Typography fontWeight={700} fontSize={13} sx={{ mb: 1 }}>
            {pickerType === "work" ? "Select a Work" : "Select a Task"}
          </Typography>
          <TextField
            fullWidth size="small" placeholder="Search..."
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            sx={{ "& .MuiInputBase-root": { borderRadius: "8px", fontSize: 13 } }}
          />
        </Box>
        <Box sx={{ overflowY: "auto", maxHeight: 220 }}>
          {pickerItems.slice(0, 20).map((item, idx) => (
            <Box
              key={idx}
              onClick={() => {
                if (pickerType === "work") setTaggedWork(item);
                else setTaggedTask(item);
                closePicker();
              }}
              sx={{
                px: 2, py: 1.2, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                "&:hover": { background: "#f8fafc" }, fontSize: 13,
                borderBottom: "1px solid #f8fafc"
              }}
            >
              {pickerType === "work" ? <MdWork size={15} color="#2563eb" /> : <MdTask size={15} color="#7c3aed" />}
              <Typography fontSize={13}>{pickerType === "work" ? item.work_name : item.name}</Typography>
            </Box>
          ))}
          {pickerItems.length === 0 && (
            <Typography fontSize={13} color="#94a3b8" sx={{ p: 2, textAlign: "center" }}>No results</Typography>
          )}
        </Box>
      </Popover>
    </Container>
  );
}
